// Calendar arithmetic, done on LOCAL calendar days and never on UTC instants.
//
// The backend stores DATE and nothing else — no time, no zone. `new Date('2026-09-07')` parses that as
// UTC MIDNIGHT, so at any positive offset (which is all of Belgium, all year) `getDate()` returns the
// 6th. Every date in this app is therefore a `YYYY-MM-DD` STRING, and the only place a `Date` object
// appears is inside these functions, constructed from local components.

export type Ymd = string;

export const ymd = (d: Date): Ymd =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Parse `YYYY-MM-DD` into a LOCAL midnight Date. Never use `new Date(string)` for this. */
export const parseYmd = (s: Ymd): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const today = (): Ymd => ymd(new Date());

export const addDays = (s: Ymd, n: number): Ymd => {
  const d = parseYmd(s);
  d.setDate(d.getDate() + n);
  return ymd(d);
};

export const daysBetween = (a: Ymd, b: Ymd): number =>
  Math.round((parseYmd(b).getTime() - parseYmd(a).getTime()) / 86400000);

/** Monday-first, because Belgium. */
export const startOfWeek = (s: Ymd): Ymd => {
  const d = parseYmd(s);
  const shift = (d.getDay() + 6) % 7;
  return addDays(s, -shift);
};
export const startOfMonth = (s: Ymd): Ymd => { const d = parseYmd(s); return ymd(new Date(d.getFullYear(), d.getMonth(), 1)); };
export const endOfMonth = (s: Ymd): Ymd => { const d = parseYmd(s); return ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0)); };

export const isWeekend = (s: Ymd): boolean => { const g = parseYmd(s).getDay(); return g === 0 || g === 6; };

const DAY_NAMES = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const MONTHS = ['januari', 'februari', 'maart', 'april', 'mei', 'juni',
                'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

export const dayLabel = (s: Ymd): string => DAY_NAMES[(parseYmd(s).getDay() + 6) % 7];
export const dayNumber = (s: Ymd): number => parseYmd(s).getDate();
export const monthLabel = (s: Ymd): string => { const d = parseYmd(s); return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`; };
export const longLabel = (s: Ymd): string => {
  const d = parseYmd(s);
  return `${DAY_NAMES[(d.getDay() + 6) % 7]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

/** Inclusive range. Used for a week strip, a month grid and a workpackage's own span alike. */
export const range = (from: Ymd, to: Ymd): Ymd[] => {
  const out: Ymd[] = [];
  for (let s = from; daysBetween(s, to) >= 0; s = addDays(s, 1)) out.push(s);
  return out;
};

/** The six-week grid a month view needs, always starting on a Monday. */
export const monthGrid = (anchor: Ymd): Ymd[] => {
  const first = startOfWeek(startOfMonth(anchor));
  return range(first, addDays(first, 41));
};

/**
 * Working days in an inclusive range — the divisor the backend actually uses.
 *
 * `assignment_daily_load` spreads an allocation over the days a resource can work, so a window that
 * straddles a weekend carries its hours on fewer days than it looks. Counting them here is what lets
 * the panel say "32 u over 2 werkdagen = 16 u per dag" BEFORE somebody saves it.
 */
export const workdaysBetween = (from: Ymd, to: Ymd): number => {
  if (daysBetween(from, to) < 0) return 0;
  let n = 0;
  for (let d = from; daysBetween(d, to) >= 0; d = addDays(d, 1)) if (!isWeekend(d)) n++;
  return n;
};

export const hours = (minutes: number): string => {
  const h = minutes / 60;
  return Number.isInteger(h) ? `${h} u` : `${h.toFixed(1).replace('.', ',')} u`;
};
